import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/useAuth';
import { ErrorBoundary } from '@/components/error-boundary';
import { AppSidebarContent } from '@/components/layout/app-sidebar-content';
import { AppTopBar } from '@/components/layout/app-top-bar';
import { ConfigHealthBanner } from '@/components/layout/config-health-banner';
import { AccountSwitcher } from '@/components/layout/account-switcher';
import { LanguageSwitcher } from '@/components/layout/language-switcher';
import { PageTitleContext } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import { Sidebar, SidebarInset, SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

/** Closes the mobile sidebar drawer whenever the route changes. */
function CloseMobileSidebarOnNavigate() {
  const { setOpenMobile } = useSidebar();
  const { pathname } = useLocation();

  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  return null;
}

/**
 * Per-route content width: the data-dense tables (shipments, excluded products) run
 * full-width; everything else (including settings and authorization, matching
 * pickup-orders/markets) caps at 960px.
 */
function contentMaxWidthClass(pathname: string): string {
  if (pathname.startsWith('/shipments') || pathname.startsWith('/excluded-products')) {
    return '';
  }
  return 'max-w-[960px]';
}

export function AppLayout() {
  const auth = useAuth();
  const { t: tAuth } = useTranslation('auth');
  const { pathname } = useLocation();
  const [title, setTitle] = useState<string | null>(null);

  const onLogout =
    auth.status === 'authenticated' && auth.mode === 'standalone' ? () => auth.logout() : null;

  return (
    <PageTitleContext.Provider value={setTitle}>
      {/* h-full + min-h-0, not the component's h-svh/min-h-svh: #root already subtracts the
          Shopify safe-area inset (index.css) and the shell must not grow past it. */}
      <SidebarProvider className="min-h-0 h-full overflow-hidden">
        <CloseMobileSidebarOnNavigate />

        <Sidebar collapsible="icon" variant="floating">
          <AppSidebarContent />
        </Sidebar>

        <SidebarInset className="min-h-0 overflow-hidden">
          <AppTopBar
            title={title ?? 'InPost'}
            actions={
              <>
                <AccountSwitcher />
                <LanguageSwitcher />
                {onLogout ? (
                  <Button variant="ghost" size="sm" onClick={onLogout}>
                    {tAuth('logout')}
                  </Button>
                ) : null}
              </>
            }
          />

          <ConfigHealthBanner />

          {/* stable scrollbar-gutter: content width must not change with scrollbar
              presence (tabs of different heights read as different page widths). */}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-8 [scrollbar-gutter:stable] md:px-10 md:py-10">
            <div className={cn('mx-auto w-full', contentMaxWidthClass(pathname))}>
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </PageTitleContext.Provider>
  );
}
